import { Button, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { Location } from "history";
import { DependencyList, PropsWithChildren, createContext, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router";
// CJS default export interop - the package exports { default: Component } as module.exports
import _NavigationPrompt from "react-router-navigation-prompt";
import { useUnload } from "src/hooks";
const NavigationPrompt = (
  "default" in _NavigationPrompt ? (_NavigationPrompt as any).default : _NavigationPrompt
) as typeof _NavigationPrompt;

/**
 * Import Location from "history" to avoid type collisions from Reacts built-in Location.
 */
type CanNavigateFn = (currentLocation: Location, nextLocation?: Location | undefined) => boolean;

export const NavigationContextProvider = ({ children }: PropsWithChildren<unknown>) => {
  const history = useHistory();
  const currentLocation = history.location;
  const navigationCheckCallbacks = useRef<CanNavigateFn[]>([]);

  const canNavigate = (currentLocation: Location, nextLocation?: Location | undefined) => {
    const result = navigationCheckCallbacks.current.every((callback) => callback(currentLocation, nextLocation));
    return result;
  };

  const getNavigationCheckCallbacks = () => [...navigationCheckCallbacks.current];

  // Setting up events to listen for browser navigation to add confirmation if `promptOnLeave` is true.
  // Unfortunately we need to handle Browser routing and React routing separately.
  // `useUnload` hooks into native browser prompts. This handles full page reloads that React-Router cannot hook into.
  // We use history.location for current location since this is the last hook to run before the component is unmounted.
  useUnload((e: BeforeUnloadEvent) => {
    if (!canNavigate(currentLocation)) {
      e.preventDefault();
      e.returnValue = "";
    }
  });

  const useRegisterNavigationCheck = (allowNavigationFn: CanNavigateFn, deps?: DependencyList | undefined) => {
    useEffect(
      () => {
        navigationCheckCallbacks.current.push(allowNavigationFn);
        return () => {
          navigationCheckCallbacks.current = navigationCheckCallbacks.current.filter(
            (callback) => callback !== allowNavigationFn,
          );
        };
      },
      // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
      // eslint-disable-next-line react-hooks/exhaustive-deps
      deps,
    );
  };

  return (
    <NavigationContext.Provider value={{ canNavigate, useRegisterNavigationCheck, getNavigationCheckCallbacks }}>
      {children}
      <NavigationPrompt
        when={(currentLocation: Location, nextLocation?: Location | undefined) =>
          !canNavigate(currentLocation, nextLocation)
        }
      >
        {({ onConfirm, onCancel }) => <ConfirmLeaveModal onConfirm={onConfirm} onCancel={onCancel} />}
      </NavigationPrompt>
    </NavigationContext.Provider>
  );
};

export const useNavigationCheck = () => useContext(NavigationContext);

type NavigationContextProps = {
  canNavigate: CanNavigateFn;
  useRegisterNavigationCheck: (allowNavigationFn: CanNavigateFn, deps?: DependencyList) => void;
  getNavigationCheckCallbacks: () => CanNavigateFn[];
};

const NavigationContext = createContext<NavigationContextProps>({
  canNavigate: () => true,
  useRegisterNavigationCheck: (_, __) => {},
  getNavigationCheckCallbacks: () => [],
});

type ConfirmLeaveModalProps = {
  onConfirm: VoidFunction;
  onCancel: VoidFunction;
};

function ConfirmLeaveModal({ onConfirm, onCancel }: ConfirmLeaveModalProps) {
  const { openModal, closeModal } = useModal();
  // useEffect to immediately open the modal.
  useEffect(
    () => {
      openModal({
        allowClosing: false,
        content: (
          <>
            <ModalHeader>Leave page?</ModalHeader>
            <ModalBody>
              <p>All changes you've made will be lost.</p>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="tertiary"
                label="Continue Editing"
                onClick={() => {
                  closeModal();
                  onCancel();
                }}
              />
              <Button
                label="Leave"
                variant="danger"
                onClick={() => {
                  onConfirm();
                  closeModal();
                }}
              />
            </ModalFooter>
          </>
        ),
      });

      return () => closeModal();
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  return <></>;
}
